<template>
  <div id="giftsSection" class="claimed-rewards">
    <h4 class="page-title text-center">جوایز من</h4>
    <LineGrow open-width="90%" />

    <!-- loading -->
    <div v-if="loading" class="state-box">در حال دریافت اطلاعات...</div>

    <!-- error -->
    <div v-else-if="error" class="state-box error">
      دریافت جوایز انجام نشد.
      <button class="retry-btn" @click="fetchRewards">تلاش دوباره</button>
    </div>

    <!-- empty -->
    <div v-else-if="!rewards.length" class="state-box">
      هنوز جایزه‌ای دریافت نکرده‌اید. در بازی‌ها شرکت کنید تا جایزه بگیرید.
    </div>

    <!-- list -->
    <div v-else class="rewards-list">
      <div
        v-for="item in rewards"
        :key="item.id"
        class="reward-card"
        :class="'type-' + item.reward_type"
      >
        <div class="reward-main">
          <span class="reward-badge">{{ typeLabel(item.reward_type) }}</span>
          <p class="reward-title">{{ rewardText(item) }}</p>
          <p class="reward-game">
            {{ item.game ? item.game.label : "" }}
            <template v-if="productText(item)">
              — {{ productText(item) }}
            </template>
          </p>
        </div>

        <div class="reward-side">
          <!-- کد تخفیف -->
          <div v-if="item.coupon && item.coupon.code" class="coupon-box">
            <span class="coupon-code">{{ item.coupon.code }}</span>
            <button class="copy-btn" @click="copyCode(item.coupon.code)">
              {{ copied === item.coupon.code ? "کپی شد" : "کپی کد" }}
            </button>
          </div>
          <span class="reward-date">{{ formatDate(item.created_at) }}</span>
        </div>
      </div>

      <!-- صفحه‌بندی (فقط اگر بیشتر از یک صفحه باشد) -->
      <section
        v-if="claimed && claimed.last_page > 1"
        class="list-pagination gifts-pagination text-center my-4 py-2"
      >
        <pagination
          :limit="1"
          :data="claimed"
          @pagination-change-page="changePage"
          :disabled="loading"
          class="justify-content-center"
        >
          <span slot="prev-nav">
            <ZPrevSvg />
          </span>
          <span slot="next-nav">
            <ZNextSvg />
          </span>
        </pagination>
      </section>
    </div>
  </div>
</template>

<script>
import LineGrow from "@/parts/Front/components/shared/LineGrow";
import ZPrevSvg from "@/parts/Front/components/shared/ZPrevSvg";
import ZNextSvg from "@/parts/Front/components/shared/ZNextSvg";

export default {
  name: "UserGifts",
  components: {
    LineGrow,
    ZPrevSvg,
    ZNextSvg,
  },
  data() {
    return {
      loading: false,
      error: false,
      copied: null,
      page: 1,
      claimed: null, // کل آبجکت صفحه‌بندی لاراول (برای کامپوننت pagination)
      rewards: [],
    };
  },
  created() {
    this.fetchRewards();
  },
  methods: {
    async fetchRewards() {
      this.loading = true;
      this.error = false;
      try {
        const res = await this.$axios.get("/customer/profile", {
          params: { page: this.page },
        });
        // ساختار پاسخ: res.data.data.claimed_rewards
        const claimed = res.data.data.claimed_rewards;
        this.claimed = claimed;
        this.rewards = claimed.data || [];
        this.page = claimed.current_page || 1;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      this.page = page;
      this.fetchRewards();
      const el = document.getElementById("giftsSection");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    },
    typeLabel(type) {
      const map = {
        coupon_percentage: "کد تخفیف",
        gift_wallet: "هدیه کیف پول",
        score: "امتیاز باشگاه",
      };
      return map[type] || "جایزه";
    },
    rewardText(item) {
      const n = Number(item.reward_value).toLocaleString("fa-IR");
      switch (item.reward_type) {
        case "coupon_percentage":
          return `${n}٪ تخفیف`;
        case "gift_wallet":
          return `${n} تومان اعتبار هدیه`;
        case "score":
          return `${n} امتیاز`;
        default:
          return n;
      }
    },
    // نام محصول و تنوع (اگر وجود داشته باشد)
    productText(item) {
      const r = item.reward || {};
      const parts = [r.product_name, item.variety_name || r.variety_name];
      return parts.filter(Boolean).join(" - ");
    },
    formatDate(iso) {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString("fa-IR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    copyCode(code) {
      const done = () => {
        this.copied = code;
        setTimeout(() => (this.copied = null), 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done);
      } else {
        const el = document.createElement("textarea");
        el.value = code;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        done();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.claimed-rewards {
  width: 100%;
  margin: 0 auto;
  padding: 16px;
  background-color: white;
}

.page-title {
  font-size: 20px;
  margin-bottom: 16px;
}

.state-box {
  padding: 32px 16px;
  text-align: center;
  border: 1px dashed #ddd;
  border-radius: 12px;
  color: #666;

  &.error {
    color: #c0392b;
    border-color: #e6b0aa;
  }
}

.retry-btn {
  display: block;
  margin: 12px auto 0;
  padding: 6px 16px;
  border: none;
  border-radius: 8px;
  background: var(--color-themepinky, #f83d84);
  color: #fff;
  cursor: pointer;
}

.rewards-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
}

.reward-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #eee;
  border-right: 4px solid #999;
  border-radius: 12px;

  &.type-coupon_percentage {
    border-right-color: var(--color-themepinky, #f83d84);
  }

  &.type-gift_wallet {
    border-right-color: var(--color-themeGreen, #2ecc71);
  }

  &.type-score {
    border-right-color: #f5a623;
  }
}

.reward-main {
  min-width: 0;
}

.reward-badge {
  display: inline-block;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 20px;
  background: #f5f5f5;
  color: #555;
}

.reward-title {
  margin: 8px 0 2px;
  font-size: 16px;
  font-weight: bold;
}

.reward-game {
  margin: 0;
  font-size: 13px;
  color: #888;
}

.reward-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.coupon-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 4px 10px;
  border: 1px dashed var(--color-themepinky, #f83d84);
  border-radius: 8px;
}

.coupon-code {
  font-family: monospace;
  font-size: 15px;
  letter-spacing: 1px;
  direction: ltr;
}

.copy-btn {
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  background: var(--color-themepinky, #f83d84);
  color: #fff;
  cursor: pointer;
}

.reward-date {
  font-size: 12px;
  color: #999;
}

.gifts-pagination {
  grid-column: 1 / -1;
}

@media (max-width: 576px) {
  .claimed-rewards {
    padding: 12px 10px;
  }

  .rewards-list {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .reward-card {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }

  .reward-title {
    font-size: 15px;
  }

  .reward-side {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    padding-top: 10px;
    border-top: 1px dashed #eee;
  }

  .coupon-box {
    flex: 1 1 auto;
    justify-content: space-between;
    min-width: 0;
  }

  .copy-btn {
    padding: 6px 12px;
  }
}
</style>
